import React, { FC, useState } from 'react';
import styled from 'styled-components';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import InverseButton from 'crehana-ui/Buttons/InverseButton';
import TextLink from 'crehana-ui/Buttons/TextLink';
import InfoOutline from 'crehana-icons/InfoOutline';
import { secondaryMain } from 'crehana-ui/styles/colors';
import buyProduct, { formatProduct } from 'Jsx/Utils/buyProduct';
import {
  eventSources,
  eventNames,
  setFbEventToLocalStorage,
} from 'Jsx/Utils/facebookTracking/facebookTracking';

import useMediaQuery from 'Jsx/Shared/Hooks/useMediaQuery';

import useMembership from './hooks/useMembership';

import PurpleCheck from 'crehana-icons/PurpleCheck';
// import PurpleDownload from 'crehana-icons/PurpleDownload';
import PurpleGraduate from 'crehana-icons/PurpleGraduate';
import PurpleFlag from 'crehana-icons/PurpleFlag';
import PurpleMessages from 'crehana-icons/PurpleMessages';
import mixpanel from 'Jsx/Utils/mixpanelTracking';
import Loader from 'crehana-ui/Loader';

import ShareLinks from '../ShareLinks';

import Text from 'crehana-ui/Text';

import { Course } from '../../types';

import BenefitsDialog from './components/BenefitsDialog';

const Root = styled.div<{ isBenefitsCardVisible: boolean }>`
  #membership-benefits {
    transition: 0.3s;
    transform: ${props =>
      props.isBenefitsCardVisible ? 'translateX(-360px)' : 'translateX(0)'};
  }

  & #arrow {
    transform: rotate(45deg);
  }
`;

const MembershipBenefits = styled.div`
  top: -32px;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    width: 314px;
    display: block;
  }
`;

interface MembershipModeProps {
  WishlistButton?: React.ReactElement;
  CartButton?: React.ReactElement;
  GiftButton?: React.ReactElement;
  course: Course;
  canRenderCourseTitle?: boolean;
  canRenderShareLinks?: boolean;
  canShowPaymentDialog?: boolean;
  showPaymentDialog?(): void;
  mixpanelPage?: string;
  prices: Course['prices'];
}

const MembershipMode: FC<MembershipModeProps> = ({
  WishlistButton,
  CartButton,
  GiftButton,
  course,
  canRenderCourseTitle = false,
  canRenderShareLinks = false,
  canShowPaymentDialog,
  showPaymentDialog,
  mixpanelPage = 'undefined page',
  prices,
}) => {
  const { membership, loading } = useMembership('LANDING_CASE_B');
  const { matches: isDesktop } = useMediaQuery('lg');
  const [isBenefitsCardVisible, setBenefitsCardVisible] = useState(false);

  if (loading || !membership) {
    return (
      <div className="py-64 w-full flex justify-center">
        <Loader size={48} />
      </div>
    );
  }
  const fbTrackPayloadPremium = {
    content_name: membership.tag,
    content_ids: membership.originalId,
    content_type: 'membership',
    value: membership?.prices?.total,
    currency: membership?.prices?.currencySymbol,
  };

  const Divider = (
    <div className="flex items-center justify-center mb-8">
      <div className="w-48 bg-gray-main" style={{ height: 1 }} />
      <Text className="mx-16 flex-shrink-0" type="body3">
        o
      </Text>
      <div className="w-48 bg-gray-main" style={{ height: 1 }} />
    </div>
  );

  const benefits = [
    {
      id: 1,
      Icon: <PurpleCheck size={32} />,
      text: 'Acceso a +500 cursos de nuestro catálogo.',
    },
    {
      id: 2,
      Icon: <PurpleFlag size={32} />,
      text: 'Cursos Premium con expertos de la industria digital.',
    },
    {
      id: 3,
      Icon: <PurpleGraduate size={28} />,
      text: 'Acceso exclusivo a carreras y certificaciones.',
    },
    {
      id: 4,
      Icon: <PurpleMessages size={32} />,
      text: 'Mentoría especializada 24/7.',
    },
    // {
    //   id: 5,
    //   Icon: <PurpleDownload size={28} />,
    //   text: 'Clases web en vivo todas las semanas.',
    // },
  ];

  const Benefits = (
    <MembershipBenefits
      id="membership-benefits"
      className="absolute px-20 py-24 bg-base-main -z-1 left-0"
    >
      <Text className="mb-16" type="subtitle3" color="white" as="p">
        Beneficios exclusivos de tu Membresía Premium
      </Text>
      <ul>
        {benefits.map(benefit => (
          <li key={benefit.id} className="flex items-center mb-12">
            <div className="flex-shrink-0">{benefit.Icon}</div>
            <Text className="ml-16" type="body4" color="white" as="p">
              {benefit.text}
            </Text>
          </li>
        ))}
      </ul>
      <div
        id="arrow"
        className="absolute block bg-base-main w-24 h-24"
        style={{ right: -12, top: '50%' }}
      />
    </MembershipBenefits>
  );

  const CourseTitle = (
    <Text className="mb-16" type="h4" as="h1">
      {course.title}
    </Text>
  );

  const monthlyPrice =
    membership.prices.currencySymbol +
    Math.floor(membership.prices.monthlyPrice).toLocaleString();

  const PremiumCopy = (
    <>
      <Text className="lg:hidden sm:text-center" type="subtitle2" as="p">
        Accede a todos los cursos por {monthlyPrice} al mes (pago anual).
      </Text>
      <Text className="hidden lg:block" type="subtitle3" as="p">
        Accede a todos los cursos por {monthlyPrice} al mes (pago anual).
      </Text>
    </>
  );

  const buyCourse = () => {
    if (canShowPaymentDialog && showPaymentDialog) {
      showPaymentDialog();
    } else {
      const product = formatProduct(course, 'course');
      const fbTrackPayload = {
        content_name: course.title,
        content_ids: course.originalId,
        content_type: 'course',
        value: prices?.price,
        currency: prices?.currencySymbol,
      };

      setFbEventToLocalStorage({
        eventName: eventNames.GO_TO_CHECKOUT,
        sourcePage: eventSources.COURSE_DETAIL,
        fbTrackPayload,
      });

      mixpanel.track(mixpanel.EVENTS.BUY, { product: 'course' }, () => {
        buyProduct(course.urlToBuyNow, product);
      });
    }
  };

  const BenefitsButton = (
    <>
      <button
        id="benefits-info-link"
        type="button"
        className="lg:hidden cursor-pointer w-full py-16 flex items-center justify-center lg:justify-start"
        onClick={() => {
          setBenefitsCardVisible(true);
        }}
        onMouseOver={() => {
          setBenefitsCardVisible(true);
        }}
        onFocus={() => {
          setBenefitsCardVisible(true);
        }}
      >
        <InfoOutline size={16} color={secondaryMain} />
        <TextLink className="ml-8">Ver más beneficios</TextLink>
      </button>
      <div
        id="benefits-info-link"
        className="hidden lg:flex cursor-pointer w-full py-16 flex items-center justify-center lg:justify-start"
        onMouseOver={() => {
          setBenefitsCardVisible(true);
        }}
        onFocus={() => {
          setBenefitsCardVisible(true);
        }}
        onMouseLeave={() => {
          setBenefitsCardVisible(false);
        }}
      >
        <InfoOutline size={16} color={secondaryMain} />
        <TextLink className="ml-8">Ver más beneficios Premium</TextLink>
      </div>
    </>
  );

  return (
    <>
      <Root
        className="bg-white lg:py-32 lg:px-24"
        isBenefitsCardVisible={isBenefitsCardVisible}
      >
        <div className="relative">
          <div className="hidden lg:block">{Benefits}</div>
          <div className="mb-16 lg:mb-12 relative z-5">
            {canRenderCourseTitle && CourseTitle}
            {PremiumCopy}
          </div>
          <div className="mx-auto" style={{ maxWidth: 400 }}>
            <PrimaryButton
              full
              size="l"
              onClick={() => {
                setFbEventToLocalStorage({
                  eventName: eventNames.GO_TO_CHECKOUT,
                  sourcePage: eventSources.COURSE_DETAIL,
                  fbTrackPayload: fbTrackPayloadPremium,
                });
                mixpanel.track(
                  mixpanel.EVENTS.BUY,
                  {
                    productType: 'membership',
                    origin: 'Course detail - Membership Colombia Promo',
                    mixpanelPage,
                  },
                  () => {
                    location.href = membership.urlCheckout;
                  },
                );
              }}
              label="Vuélvete Premium"
            />
            {BenefitsButton}
            {Divider}
            <InverseButton
              full
              className="px-24"
              gray
              size="l"
              label={`Compra el curso por ${course.prices.currencySymbol +
                course.prices.price.toLocaleString()}`}
              onClick={buyCourse}
            />
          </div>
        </div>
        <div className="mt-32">
          {canRenderShareLinks ? (
            <ShareLinks
              course={course}
              showAddToCartButton
              hideWishlist={false}
              hideGiftButton={false}
              hideShareButton
            />
          ) : (
            <div className="flex justify-center">
              {CartButton}
              {WishlistButton}
              {GiftButton}
            </div>
          )}
        </div>
      </Root>
      {isBenefitsCardVisible && !isDesktop && (
        <BenefitsDialog
          isBenefitsCardVisible={isBenefitsCardVisible}
          setBenefitsCardVisible={setBenefitsCardVisible}
          membershipUrlCheckout={membership.urlCheckout}
          benefits={benefits}
        />
      )}
    </>
  );
};

export default MembershipMode;
